// A pack animal's chest (M19 remainder). PURE (ENG-1).
//
// A donkey or a mule with a chest strapped on IS a container — you open it from
// beside the animal and it walks off carrying whatever you put in. The storage
// lives on the ENTITY rather than on a block, which is the only thing that makes
// this different from the chest block entity: there is no cell to key it by, so
// the host keys it by entity id and hands the pack around by reference.
import { type ItemStack, type Slots, emptyStack, isEmpty, cloneStack } from '../inventory';

export interface MountPack {
  /** Fixed-length; length IS the animal's slot count (its def's `chestSlots`). */
  items: (ItemStack | null)[];
}

export function newPack(size: number): MountPack {
  return { items: Array.from({ length: size }, () => null) };
}

/** Non-empty stacks a dead (or unloaded) pack animal scatters. */
export function packContents(p: MountPack): ItemStack[] {
  return p.items.filter((s): s is ItemStack => s != null && !isEmpty(s));
}

/** A `Slots` view over a pack, for the container window. */
export function packSlots(p: MountPack, maxStackOf: (item: number) => number): Slots {
  return {
    size: p.items.length,
    get: (i) => p.items[i] ?? emptyStack(),
    set: (i, s) => { p.items[i] = isEmpty(s) ? null : s; },
    maxStackOf: (item) => maxStackOf(item),
    accepts: () => true,
  };
}

/** Deep-clone a pack (persistence snapshot). */
export function clonePack(p: MountPack): MountPack {
  return { items: p.items.map((s) => (s && !isEmpty(s) ? cloneStack(s) : null)) };
}

/** Resize a pack, keeping what fits — used when a saved pack meets a def whose
 *  slot count has changed, so a world never loses items to a retune. Anything that
 *  no longer fits is returned so the host can drop it rather than silently eat it. */
export function resizePack(p: MountPack, size: number): { pack: MountPack; overflow: ItemStack[] } {
  const pack = newPack(size);
  const overflow: ItemStack[] = [];
  for (let i = 0; i < p.items.length; i++) {
    const s = p.items[i];
    if (s == null || isEmpty(s)) continue;
    if (i < size) pack.items[i] = s; else overflow.push(s);
  }
  return { pack, overflow };
}
